import { AvatarConfig } from "../avatar/common/player/type";
import { ResourceType } from "../base/loader/type";
import { SmartScenarioBuilders } from "../avatar/common/player/util";
import { defaultConfig } from "./default.config";
import { blendShapeIndex, vrmBlendShapeIndex } from "../avatar/common/lipsync/constants";
import type { CameraConfig } from '../base/camera/type';
import type { LightingConfig } from '../base/light/type';
import type { RendererConfig } from '../base/renderer/type';
import type { LoaderConfig } from '../base/loader/type';
import type { ScenarioConfigs } from '../avatar/common/animation/type';

// Vroid 리소스 설정
export const vroidResources: ResourceType[] = [
  { name: "vroid", type: "model_only", path: "/vrm/girl.vrm", format: "vrm" },
  { name: "greeting_entrance01", type: "animation_only", path: "/vrma/vroid_girl@greeting_entrance01.vrma", format: "vrma" },
  { name: "idle_standby01", type: "animation_only", path: "/vrma/vroid_girl@idle_standby01.vrma", format: "vrma" },
  { name: "idle_standby02", type: "animation_only", path: "/vrma/vroid_girl@idle_standby02.vrma", format: "vrma" },
  { name: "input_listening01", type: "animation_only", path: "/vrma/vroid_girl@input_listening01.vrma", format: "vrma" },
  { name: "input_listening02", type: "animation_only", path: "/vrma/vroid_girl@input_listening02.vrma", format: "vrma" },
  { name: "input_listening03", type: "animation_only", path: "/vrma/vroid_girl@input_listening03.vrma", format: "vrma" },
  { name: "input_processing01", type: "animation_only", path: "/vrma/vroid_girl@input_processing01.vrma", format: "vrma" },
  // 임시 Speaking 더미 시나리오
  { name: "response_speaking01", type: "animation_only", path: "/vrma/vroid_girl@response_speaking01.vrma", format: "vrma" },
  { name: "response_speaking02", type: "animation_only", path: "/vrma/vroid_girl@response_speaking02.vrma", format: "vrma" },
  { name: "response_speaking03", type: "animation_only", path: "/vrma/vroid_girl@response_speaking03.vrma", format: "vrma" },
  { name: "response_speaking04", type: "animation_only", path: "/vrma/vroid_girl@response_speaking04.vrma", format: "vrma" },
  { name: "response_speaking05", type: "animation_only", path: "/vrma/vroid_girl@response_speaking05.vrma", format: "vrma" }
];

/**
 * Vroid 시나리오 설정 - 스마트 빌더 사용
 */
const VROID_SCENARIOS: ScenarioConfigs<string> = {
  entrance: SmartScenarioBuilders.auto("greeting_entrance", vroidResources, {
    preferredMode: 'once',
    description: "입장 애니메이션",
    nextScenario: 'speaking'
  }),

  // start: SmartScenarioBuilders.auto("greeting_start", vroidResources, {
  //   preferredMode: 'once',
  //   description: "시작 애니메이션"
  // }),

  toss: SmartScenarioBuilders.auto("idle_standby", vroidResources, {
    preferredMode: 'loop',
    description: "결제 대기 상태"
  }),

  processing: SmartScenarioBuilders.auto("input_processing", vroidResources, {
    preferredMode: 'last_loop',
    description: "입력 처리 중 상태"
  }),

  listening: SmartScenarioBuilders.auto("input_listening", vroidResources, {
    preferredMode: 'last_loop',
    description: "사용자 입력을 듣는 상태"
  }),

  speaking: SmartScenarioBuilders.auto("response_speaking", vroidResources, {
    preferredMode: 'random',
    description: "말하기 상태 (임시로 idle_standby 사용)"
  }),
};

// 카메라 설정
const vroidCameraConfig: CameraConfig = {
  ...defaultConfig.camera,
  cameras: [
    {
      name: "vroid_main_camera",
      type: "PerspectiveCamera",
      active: true,
      fov: 40,
      position: { x: 0, y: 1, z: 2 },
      rotation: {
        x: 0,
        y: 0,
        z: 0
      },
      near: 0.1,
      far: 200
    }
  ]
};

// 조명 설정 - 최소한의 조명만 설정
const vroidLightConfig: LightingConfig = {
  ...defaultConfig.lighting,
};

// 렌더러 설정 - bloom 없음
const vroidRendererConfig: RendererConfig = {
  ...defaultConfig.renderer,
  bloom: undefined
};

// 로더 설정
const vroidLoaderConfig: LoaderConfig = {
  ...defaultConfig.loader,
  options: {
    ...defaultConfig.loader.options,
    isVRM: true,
  }
};

// 표준 BlendShape -> VRM BlendShape 매핑 테이블 및 메타데이터 생성
const createVrmMappingData = () => {
  const mappingTable: { [key: number]: number } = {};
  const usedVrmIndices = new Set<number>();

  // 공통 BlendShape들에 대해 매핑
  Object.entries(blendShapeIndex).forEach(([name, standardIndex]) => {
    if (name in vrmBlendShapeIndex) {
      const vrmIndex = vrmBlendShapeIndex[name as keyof typeof vrmBlendShapeIndex];
      mappingTable[standardIndex] = vrmIndex;
      usedVrmIndices.add(vrmIndex);
    }
  });

  // 실제 사용되는 인덱스들을 정렬된 배열로 변환
  const sortedIndices = Array.from(usedVrmIndices).sort((a, b) => a - b);

  // VRM 인덱스 -> 압축된 인덱스 매핑 테이블 생성
  const vrmToCompactMapping: { [key: number]: number } = {};
  sortedIndices.forEach((vrmIndex, compactIndex) => {
    vrmToCompactMapping[vrmIndex] = compactIndex;
  });

  // 최종 매핑 테이블: 표준 인덱스 -> 압축된 인덱스
  const compactMappingTable: { [key: number]: number } = {};
  Object.entries(mappingTable).forEach(([standardIndex, vrmIndex]) => {
    compactMappingTable[parseInt(standardIndex)] = vrmToCompactMapping[vrmIndex];
  });

  return {
    mappingTable: compactMappingTable,
    totalBlendshapeCount: sortedIndices.length, // 실제 사용되는 개수만
    usedVrmIndices: sortedIndices
  };
};

// VRM 매핑 데이터 생성
const vrmMappingData = createVrmMappingData();

// 날나니 필터링 인덱스를 VRM 인덱스로 변환하는 함수
const createVrmFilterIndices = () => {
  // 날나니가 필터링하는 표준 인덱스들 (입 모양과 관련된 주요 BlendShape들)
  const nalnaniFilterIndices = [22, 24, 31, 33, 34, 37, 40, 41, 43, 44, 45, 46, 47, 48];

  // 표준 인덱스 이름들
  const standardIndexNames = [
    'jawForward',    // 22
    'jawOpen',       // 24
    'mouthFunnel',   // 31
    'mouthLowerDownLeft',  // 33
    'mouthLowerDownRight', // 34
    'mouthPucker',   // 37
    'mouthRollUpper', // 40
    'mouthShrugLower', // 41
    'mouthSmileLeft', // 43
    'mouthSmileRight', // 44
    'mouthStretchLeft', // 45
    'mouthStretchRight', // 46
    'mouthUpperUpLeft', // 47
    'mouthUpperUpRight' // 48
  ];

  // VRM 인덱스로 변환
  const vrmFilterIndices: number[] = [];

  nalnaniFilterIndices.forEach((standardIndex, i) => {
    const shapeName = standardIndexNames[i];
    if (shapeName in vrmBlendShapeIndex) {
      const vrmIndex = vrmBlendShapeIndex[shapeName as keyof typeof vrmBlendShapeIndex];
      vrmFilterIndices.push(vrmIndex);
    }
  });

  return vrmFilterIndices.sort((a, b) => a - b);
};

// VRM 필터링 인덱스 생성
const vrmFilterIndices = createVrmFilterIndices();

/**
 * Vroid 아바타 설정
 */
export const vroidAvatarConfig: AvatarConfig = {
  type: 'vroid' as const,
  modelKey: 'vroid',
  resources: vroidResources,
  scenarioConfigs: VROID_SCENARIOS,
  rotation: {
    x: 0,
    y: 180 * Math.PI / 180,
    z: 0
  },
  lipsync: {
    indices: vrmFilterIndices, // 날나니와 동일한 필터링 적용
    processingType: 'vrm_filter',
    config: {
      indices: vrmFilterIndices, // 필터링할 VRM 인덱스들
      mappingTable: vrmMappingData.mappingTable,
      totalBlendshapeCount: vrmMappingData.totalBlendshapeCount,
      usedVrmIndices: vrmMappingData.usedVrmIndices
    }
  },
};

// Vroid 통합 설정 인터페이스
export interface VroidConfig {
  loader: LoaderConfig;
  camera: CameraConfig;
  lighting: LightingConfig;
  renderer: RendererConfig;
  avatar: AvatarConfig;
}

/**
 * Vroid 설정 통합 객체
 */
export const vroidConfig: VroidConfig = {
  loader: vroidLoaderConfig,
  camera: vroidCameraConfig,
  lighting: vroidLightConfig,
  renderer: vroidRendererConfig,
  avatar: vroidAvatarConfig
};
